import React, { FC, useMemo, memo } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import pluralize from 'pluralize';
import _isUndefined from 'lodash/isUndefined';
import _isNumber from 'lodash/isNumber';
import _truncate from 'lodash/truncate';
import { gqlMediaCount, gqlMediaTypeCodeEnum as MEDIA_TYPE } from '@minecraft.graphql-types';
import { CoreTheme as theme } from '@minecraft.themes';
import Button from '@minecraft.button';
import Icon from '@minecraft.icon';
import UncontrolledTooltip from '@minecraft.uncontrolled-tooltip';
import { AppType } from '@blocs.common';
import { useTranslation } from '@blocs.i18n';
import { BasicContainer, PrimaryButton } from '@minecraft.atoms';
import { BaseballCard } from '@minecraft.molecules';
import { ID, NoteType } from '../../../shared/types';
import { PickButtonCD, PickButtonReps } from '../../PickButton';
import TalentCardFCNote from '../TalentCardFCNote';

const DetailsLinkWrapper = styled.div`
  display: flex;
  padding: 0.5rem;

  border-top: 1px solid ${(props): string => props.theme.colors.primary.ltGrey1};

  a {
    font-size: 0.75rem;
    font-weight: 500;
    margin: auto;
  }
`;

const SingleDetailsLinkWrapper = styled(DetailsLinkWrapper)`
  padding: 0.5rem;
  flex: auto;
`;

const StyledFeebackButton = styled(Button)`
  font-size: 0.75rem;
  padding: 0;
  min-width: auto;
  height: auto;

  &,
  &:hover {
    color: ${(props): string => props.theme.colors.primary.primary};
    text-decoration: none;
  }
`;

export const ChatIcon = styled(Icon)`
  width: 1rem;
  height: 1rem;
  margin-right: 0.25rem;
`;

const Feedback = styled.div`
  align-items: center;
  border-top: 0.0625rem solid ${(props): string => props.theme.colors.primary.ltGrey1};
  display: flex;
  padding: 0.5rem;
  justify-content: space-between;
`;

export const CommentCount = styled.div<{ $commentCount?: number }>`
  align-items: center;
  display: flex;
  font-size: 0.75rem;
  font-weight: bold;
  justify-content: center;
  cursor: pointer;

  ${(props) =>
    _isNumber(props.$commentCount) &&
    props.$commentCount <= 0 &&
    `
    color: ${props.theme.colors.primary.ltGrey1};
    cursor: default;
  `}
`;

const CardActionButtons = styled.div`
  border-top: 0.0625rem solid ${(props) => props.theme.colors.primary.ltGrey1};
  display: flex;
`;

const ActionButtonGroup = styled.div`
  border-right: 0.0625rem solid ${(props): string => props.theme.colors.primary.ltGrey1};
  display: flex;
  flex: 0 0 50%;
  justify-content: center;
  align-items: center;
  margin: 0.125rem 0;
`;

const ActionButton = styled.button`
  display: flex;
  background: transparent;
  border: none;
  padding: 0;
  width: 1.125rem;
  height: 1.125rem;

  &:not(:last-child) {
    margin-right: 1rem;
  }
`;

export const StyledActionIcon = styled(Icon)`
  width: 1.125rem;
  height: 1.125rem;
`;

type SelectionTagEnabled = {
  selectionTagCode: string;
  onSelectionTagChange: (value: string) => void;
};
type SelectionTagDisabled = {
  selectionTagCode?: never;
  onSelectionTagChange?: never;
};
type SelectionTagProps = SelectionTagEnabled | SelectionTagDisabled;

type Props = SelectionTagProps & {
  appType: AppType;
  presentationId?: ID;
  guid?: string;
  isPublic: boolean;
  showPickButton?: boolean;
  showImportPhotos?: boolean;
  disableImportPhotos?: boolean;
  showFeedback?: boolean;
  submissionId: ID;
  profileId?: ID;
  groupTalentId?: ID;
  folderGuid: string;
  userName?: string;
  detailLinkPath?: string;
  commentCount: number;
  pickCount: number;
  myPick: any;
  pickedBy: any[];
  fastCaptureNotes?: readonly NoteType[];
  submissionNote: string;
  submissionMediaCount: gqlMediaCount[];
  onFeedbackModalOpen: (groupTalentId: ID) => void;
  handleUpdateSubmission: (data) => void;
  handleUserNameModalToggle: () => void;
  handleQuickViewOverlayToggle: () => void;
  openImportPhotoModal: (submissionId: number) => void;
};

const getMediaCount = (type: string, mediaCount: Readonly<gqlMediaCount[]> = []): number => {
  return mediaCount?.find((mc) => mc.type?.code === type)?.count || 0;
};

const TalentCardFooter: FC<Props> = ({
  appType,
  presentationId,
  guid,
  isPublic,
  showPickButton = false,
  showImportPhotos = false,
  disableImportPhotos = false,
  showFeedback = false,
  profileId,
  submissionId,
  groupTalentId,
  folderGuid,
  userName,
  detailLinkPath,
  commentCount,
  pickCount,
  myPick,
  pickedBy,
  fastCaptureNotes,
  submissionNote,
  submissionMediaCount,
  onFeedbackModalOpen,
  handleUpdateSubmission,
  handleUserNameModalToggle,
  handleQuickViewOverlayToggle,
  openImportPhotoModal,
  selectionTagCode,
  onSelectionTagChange,
}) => {
  const { t } = useTranslation();

  const { submissionMediaDisabled, submissionVideoCount, submissionAudioCount, submissionPhotoCount } = useMemo(() => {
    const mediaDisabled = !submissionMediaCount || submissionMediaCount?.length <= 0;
    const videoCount = getMediaCount(MEDIA_TYPE.VIDEO, submissionMediaCount);
    const audioCount = getMediaCount(MEDIA_TYPE.AUDIO, submissionMediaCount);
    const photoCount = getMediaCount(MEDIA_TYPE.PHOTO, submissionMediaCount);

    return {
      submissionMediaDisabled: mediaDisabled,
      submissionVideoCount: videoCount,
      submissionAudioCount: audioCount,
      submissionPhotoCount: photoCount,
    };
  }, [submissionMediaCount]);

  const handleFeedbackModalOpen = (): void => {
    if (groupTalentId) {
      onFeedbackModalOpen(groupTalentId);
    }
  };

  const handleImportPhotoModalOpen = (): void => {
    if (groupTalentId) {
      openImportPhotoModal(groupTalentId);
    }
  };

  const handleQuickViewOverlayOpen = (event): void => {
    handleQuickViewOverlayToggle();

    // hide tooltip when opening overlay
    event.currentTarget.blur();
  };

  const submissionNoteId = `submission-note-${submissionId}`;
  const submissionMediaId = `submission-media-${submissionId}`;
  const PickButton = appType === AppType.Reps ? PickButtonReps : PickButtonCD;
  const submissionNoteColor = submissionNote ? theme.colors.primary.primary : theme.colors.primary.ltGrey1;
  const submissionMediaColor = !submissionMediaDisabled ? theme.colors.primary.primary : theme.colors.primary.ltGrey1;
  const showSelectionTag = typeof selectionTagCode === 'string' && typeof onSelectionTagChange === 'function';

  return (
    <>
      {showImportPhotos && (
        <BasicContainer mx="2" mb="2">
          <PrimaryButton
            type="button"
            label={t('common:button.importPhotos')}
            variant="secondary"
            className="cn_lg:w-full"
            onClick={handleImportPhotoModalOpen}
            disabled={disableImportPhotos}
            full
          />
        </BasicContainer>
      )}
      {showPickButton && (
        <BasicContainer mx="2" mb="2">
          <PickButton
            withTooltip
            isPublic={isPublic}
            entityId={appType === AppType.Reps ? profileId : groupTalentId}
            submissionId={submissionId}
            presentationId={presentationId}
            presentationGuid={guid}
            folderGuid={folderGuid}
            userName={userName}
            pickCount={pickCount}
            myPick={myPick}
            pickedBy={pickedBy}
            handleUpdateSubmission={handleUpdateSubmission}
            handleUserNameModalToggle={handleUserNameModalToggle}
            qaId="talent-card-pick-button"
          />
        </BasicContainer>
      )}
      {showSelectionTag && (
        <BasicContainer mx="2" mb="2">
          <BaseballCard.SelectionTags
            selectedValue={selectionTagCode}
            onSelected={(onSelected) => onSelectionTagChange(onSelected)}
            mdProps={{ size: 'sm' }}
          />
        </BasicContainer>
      )}
      {(!!fastCaptureNotes || !!detailLinkPath) && (
        <>
          {!!fastCaptureNotes && <TalentCardFCNote fastCaptureNotes={fastCaptureNotes} />}
          {!!detailLinkPath &&
            (showPickButton || showFeedback ? (
              <DetailsLinkWrapper>
                <Link to={detailLinkPath}>{t('common:button.details')}</Link>
              </DetailsLinkWrapper>
            ) : (
              <CardActionButtons>
                {!_isUndefined(submissionNote) && !_isUndefined(submissionMediaCount) && (
                  <ActionButtonGroup>
                    <ActionButton
                      aria-label={t('common:profileEdit.submissionNote')}
                      id={submissionNoteId}
                      disabled={!submissionNote}
                      onClick={handleQuickViewOverlayOpen}
                    >
                      <StyledActionIcon name="submission-note" color={submissionNoteColor} />
                    </ActionButton>
                    {!!submissionNote && (
                      <UncontrolledTooltip placement="top" target={submissionNoteId}>
                        {_truncate(submissionNote, { length: 200 })}
                      </UncontrolledTooltip>
                    )}
                    <ActionButton
                      id={submissionMediaId}
                      disabled={submissionMediaDisabled}
                      onClick={handleQuickViewOverlayOpen}
                    >
                      <StyledActionIcon name="submission-media" color={submissionMediaColor} />
                    </ActionButton>
                    {!submissionMediaDisabled && (
                      <UncontrolledTooltip placement="top" target={submissionMediaId}>
                        {submissionPhotoCount > 0 && <div>{pluralize('Photo', submissionPhotoCount, true)}</div>}
                        {submissionVideoCount > 0 && <div>{pluralize('Video', submissionVideoCount, true)}</div>}
                        {submissionAudioCount > 0 && <div>{submissionAudioCount} Audio</div>}
                      </UncontrolledTooltip>
                    )}
                  </ActionButtonGroup>
                )}
                <SingleDetailsLinkWrapper>
                  <Link to={detailLinkPath}>{t('common:button.details')}</Link>
                </SingleDetailsLinkWrapper>
              </CardActionButtons>
            ))}
        </>
      )}
      {showFeedback && (
        <Feedback data-testid="feedback-section">
          <CommentCount onClick={userName ? handleFeedbackModalOpen : handleUserNameModalToggle}>
            <ChatIcon name="chat-filled" />
            {commentCount}
          </CommentCount>
          <StyledFeebackButton
            role="button"
            color="link"
            disabled={!groupTalentId}
            onClick={userName ? handleFeedbackModalOpen : handleUserNameModalToggle}
            data-qa-id="talent-card-leave-feedback"
          >
            {t('common:button.leaveFeedback')}
          </StyledFeebackButton>
        </Feedback>
      )}
    </>
  );
};

export default memo(TalentCardFooter);
